import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { SETTINGS_PATH_EMAIL_DISPATCH } from '../../../utils/settingsRoutes';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';

const fieldLabel = SETTINGS_FIELD_LABEL;
const inputClass = `${SETTINGS_INPUT_CLASS} bg-app-surface`;
const btnOutline = SETTINGS_BTN_OUTLINE;
const btnPrimary = SETTINGS_BTN_PRIMARY;

export function InvoiceForwardingSettingsSection() {
  const location = useLocation();
  const emailDispatchPath = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    const prefix = match ? match[0] : '/i/default';
    return `${prefix}${SETTINGS_PATH_EMAIL_DISPATCH}`;
  }, [location.pathname]);

  const [email, setEmail] = React.useState('');
  const [draft, setDraft] = React.useState('');
  const [hasMail, setHasMail] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    setSaved(false);
    fetch('/api/settings/invoice-forwarding', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          throw new Error((await r.json().catch(() => null))?.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return r.json() as Promise<{ accounting_forward_email?: string | null; has_connected_mail?: boolean }>;
      })
      .then((d) => {
        if (cancelled) return;
        const value = String(d.accounting_forward_email ?? '').trim();
        setEmail(value);
        setDraft(value);
        setHasMail(Boolean(d.has_connected_mail));
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/settings/invoice-forwarding', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ accounting_forward_email: draft.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Speichern fehlgeschlagen');
      const nextEmail = String((d as { accounting_forward_email?: string | null }).accounting_forward_email ?? '').trim();
      setEmail(nextEmail);
      setDraft(nextEmail);
      setHasMail(Boolean((d as { has_connected_mail?: boolean }).has_connected_mail));
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
        <SettingsCard title="Rechnungsweiterleitung">
          <InlineLoadingState label="Lade Einstellungen…" />
        </SettingsCard>
      </div>
    );
  }

  const dirty = editing && draft.trim() !== email;

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      {!hasMail && email ? (
        <div className="p-3 rounded-lg bg-amber-50 text-amber-900 text-sm border border-amber-200">
          Für die automatische Weiterleitung muss unter{' '}
          <NavLink to={emailDispatchPath} className="font-medium underline">
            E-Mail Versand
          </NavLink>{' '}
          mindestens ein verbundenes Konto hinterlegt sein.
        </div>
      ) : null}

      <SettingsCard
        title="Rechnungsweiterleitung"
        description="Nach dem Versand einer Rechnung an den Kunden wird automatisch eine Kopie mit PDF an diese Adresse gesendet (Rechnung, Abschlags-, Schluss-, Storno- und Gutschrift). Angebote und Mahnungen sind ausgenommen."
        actions={
          <>
            <SettingsCardSavedStatus saved={saved && !editing} />
            {editing ? (
              <>
                <button
                  type="button"
                  className={btnOutline}
                  onClick={() => {
                    setDraft(email);
                    setEditing(false);
                    setError(null);
                  }}
                >
                  Abbrechen
                </button>
                <button type="button" className={btnPrimary} disabled={saving || !dirty} onClick={() => void save()}>
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className={btnOutline}
                onClick={() => {
                  setDraft(email);
                  setEditing(true);
                  setSaved(false);
                }}
              >
                Bearbeiten
              </button>
            )}
          </>
        }
      >
        {error ? (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
        ) : null}
        {editing ? (
          <div className="space-y-3 max-w-md">
            <div>
              <label className={`block ${fieldLabel} mb-1.5`}>E-Mail-Adresse Buchhaltung</label>
              <input
                type="email"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="buchhaltung@firma.de"
                className={inputClass}
                autoComplete="email"
              />
              <p className="text-xs text-app-text-subtle mt-1.5">Leer lassen, um die Weiterleitung zu deaktivieren.</p>
            </div>
          </div>
        ) : (
          <div>
            <p className={fieldLabel}>E-Mail-Adresse Buchhaltung</p>
            <p className="text-sm text-app-text mt-1.5">{email ? email : 'Nicht konfiguriert'}</p>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
